import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { CheckIcon } from "@heroicons/react/24/solid";
import { formatNumber } from "../../utils/formatters";
import useTradingStore from "../../store/useTradingStore";
import useWalletStore from "../../store/useWalletStore";
import useMarketStore from "../../store/useMarketStore";
import API_CONFIG from "../../config/api";

// Custom CSS to hide number input spinners and override global styles
const customStyles = `
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  input[type="number"] {
    -moz-appearance: textfield;
  }
  
  /* Override global input styles for trading interface inputs */
  .trading-interface input[type="number"],
  .trading-interface input[type="text"] {
    background: #ffffff !important;
    color: #000000 !important;
  }
  
  .trading-interface input[type="number"]:focus,
  .trading-interface input[type="text"]:focus {
    background: #ffffff !important;
    color: #000000 !important;
  }
  
  .trading-interface input[type="number"]::placeholder,
  .trading-interface input[type="text"]::placeholder {
    color: #4b5563 !important;
  }
`;

const TradingInterface = ({ selectedOrder }) => {
  const [activeTab, setActiveTab] = useState("buy");
  const [orderType, setOrderType] = useState("limit");
  const [marketPrice, setMarketPrice] = useState(0);
  const [marketData, setMarketData] = useState(null);

  const { selectedPair, ticker } = useTradingStore();

  const { balances } = useWalletStore();

  const {
    placeLimitOrder,
    isPlacingOrder,
    error: marketError,
    successMessage,
    clearError,
    clearSuccessMessage,
  } = useMarketStore();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange", // Trigger validation on every change
    defaultValues: {
      price: "",
      quantity: "",
      total: "",
    },
  });

  const watchPrice = watch("price");
  const watchQuantity = watch("quantity");

  // Handle market store errors and success messages
  useEffect(() => {
    if (marketError) {
      toast.error(marketError);
      clearError();
    }
  }, [marketError, clearError]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      clearSuccessMessage();
    }
  }, [successMessage, clearSuccessMessage]);

  useEffect(() => {
    if (selectedOrder) {
      setValue("price", selectedOrder.price);
      if (selectedOrder.type) {
        setActiveTab(selectedOrder.type);
      }
    }
  }, [selectedOrder, setValue]);

  // Recalculate total when switching between market and limit orders
  useEffect(() => {
    if (orderType === "market" && watchQuantity) {
      // Force re-render of total calculation for market orders
      const total = (getCurrentPrice() * parseFloat(watchQuantity)).toFixed(2);
      setValue("total", total);
    }
  }, [orderType, watchQuantity, setValue]);

  // Update total when ticker price changes for market orders
  useEffect(() => {
    if (orderType === "market" && watchQuantity && ticker[selectedPair]) {
      const total = (getCurrentPrice() * parseFloat(watchQuantity)).toFixed(2);
      setValue("total", total);
    }
  }, [ticker, selectedPair, orderType, watchQuantity, setValue]);

  // Fetch market price from new API - always fetch for both market and limit orders
  useEffect(() => {
    const fetchMarketPrice = async () => {
      if (selectedPair) {
        try {
          const response = await fetch(
            `${API_CONFIG.BASE_URL}/v1/homepage-external-price/`
          );
          const data = await response.json();

          if (data && data.pairs_data && data.pairs_data[selectedPair]) {
            const price = data.pairs_data[selectedPair].price;
            setMarketData(data);
            setMarketPrice(price);
            console.log(`✅ Fetched price for ${selectedPair}: $${price}`);
          } else {
            console.warn("Price data not found for pair:", selectedPair);
            // Fallback to local ticker price
            if (ticker[selectedPair]) {
              setMarketPrice(ticker[selectedPair].last_price || 0);
            }
          }
        } catch (error) {
          console.error("Error fetching market price:", error);
          // Fallback to local ticker price
          if (ticker[selectedPair]) {
            setMarketPrice(ticker[selectedPair].last_price || 0);
          }
        }
      }
    };

    fetchMarketPrice();

    // Auto-refresh price every 30 seconds
    let interval;
    if (selectedPair) {
      interval = setInterval(fetchMarketPrice, 30000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [selectedPair, ticker]);

  // Update total when quantity changes for market orders
  useEffect(() => {
    if (orderType === "market" && watchQuantity) {
      const total = (getCurrentPrice() * parseFloat(watchQuantity)).toFixed(2);
      setValue("total", total);
    }
  }, [watchQuantity, orderType, setValue]);

  const getCurrentPrice = () => {
    // Always use market price from API (fetched for both market and limit orders)
    return marketPrice || 0;
  };

  const onSubmit = async (data) => {
    try {
      // Double-check sell limit order price restriction before submitting
      if (activeTab === "sell" && orderType === "limit") {
        const currentPrice = getCurrentPrice();
        const minAllowedPrice = currentPrice * 0.9;
        const enteredPrice = parseFloat(data.price);

        if (currentPrice > 0 && enteredPrice < minAllowedPrice) {
          toast.error(
            `Price too low! Minimum allowed: ${formatNumber(
              minAllowedPrice,
              2
            )} ${selectedPair?.split("-")[1] || "USDT"}`
          );
          return;
        }
      }

      const orderData = {
        pair: selectedPair,
        type: 0,
        operation: activeTab === "buy" ? 0 : 1,
        quantity: data.quantity,
        price: orderType === "market" ? getCurrentPrice() : data.price,
      };

      // Use the same API for both market and limit orders
      await placeLimitOrder(orderData);

      // If we get here, the order was successful
      reset();
    } catch (error) {
      // This catch block is now just a fallback since the store handles errors
      // The main error handling is done in the useEffect that watches marketError
      console.error("Unexpected error in onSubmit:", error);
    }
  };

  return (
    <>
      <style>{customStyles}</style>
      <div className="trading-interface h-full overflow-y-auto bg-gray-900 flex flex-col p-3 sm:p-4">
        {/* Buy/Sell Toggle */}
        <div className="mb-4 sm:mb-6">
          <div className="flex bg-gray-800 rounded-lg p-1 space-x-1">
            <button
              onClick={() => setActiveTab("buy")}
              className={`flex-1 py-2 sm:py-3 px-4 sm:px-6 text-xs sm:text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === "buy"
                  ? "bg-[#0E1696] !text-white shadow-lg transform scale-105"
                  : "bg-transparent text-white hover:text-white hover:bg-gray-700"
              }`}
            >
              <div className="flex items-center justify-center space-x-1 sm:space-x-2">
                {/* <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${activeTab === 'buy' ? 'bg-white' : 'bg-green-500'}`}></div> */}
                <span>Buy</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab("sell")}
              className={`flex-1 py-2 sm:py-3 px-4 sm:px-6 text-xs sm:text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === "sell"
                  ? "bg-red-600 text-white shadow-lg transform scale-105"
                  : "bg-transparent text-white hover:text-white hover:bg-gray-700"
              }`}
            >
              <div className="flex items-center justify-center space-x-1 sm:space-x-2">
                {/* <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${activeTab === 'sell' ? 'bg-white' : 'bg-red-500'}`}></div> */}
                <span>Sell</span>
              </div>
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 sm:space-y-6">
          {/* Order Type Selection */}
          <div>
            <div className="flex space-x-2 bg-gray-800 rounded-lg p-1 border border-gray-600">
              <button
                onClick={() => setOrderType("limit")}
                className={`flex-1 py-1 sm:py-1 px-3 sm:px-4 text-xs sm:text-sm font-medium rounded-md transition-all duration-200 flex items-center justify-center gap-2 ${
                  orderType === "limit"
                    ? " !text-red-500 transform scale-105"
                    : "text-green-500 "
                }`}
              >
                {orderType === "limit" && (
                  <CheckIcon className="w-4 h-4" />
                )}
                Limit
              </button>
              <div className="w-px h-7 bg-gray-600"></div>
              <button
                onClick={() => setOrderType("market")}
                className={`flex-1 py-1 sm:py-1 px-3 sm:px-4 text-xs sm:text-sm font-medium rounded-md transition-all duration-200 flex items-center justify-center gap-2 ${
                  orderType === "market"
                    ? " !text-white-500 transform scale-105"
                    : "text-green-500 "
                }`}
              >
                {orderType === "market" && (
                  <CheckIcon className="w-4 h-4" />
                )}
                Market
              </button>
            </div>

            {/* Current Market Price Display */}
            {marketPrice > 0 && (
              <div className="mt-2 ">
                <div className="flex items-end justify-end space-x-2">
                  <span className="text-white  text-xs font-medium">
                    {orderType === "market"
                      ? "Current Price:"
                      : "Market Price:"}
                  </span>
                  <span className="text-white text-xs font-semibold">
                    ${formatNumber(marketPrice, 2)}{" "}
                    {selectedPair?.split("-")[1] || "USDT"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4 sm:space-y-6"
          >
            {/* Price Input (only for limit orders) */}
            {orderType === "limit" && (
              <div>
                <div className="flex justify-between items-center mb-2 sm:mb-3">
                  <label className="text-sm sm:text-base font-semibold text-white flex items-center">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full mr-2"></span>
                    Price
                  </label>
                </div>
                <div className="relative">
                  <input
                    {...register("price", {
                      required:
                        orderType === "limit" ? "Price is required" : false,
                      min: {
                        value: 0.01,
                        message: "Price must be greater than 0",
                      },
                      validate: (value) => {
                        // Validate sell limit orders - prevent orders below 90% of current price
                        if (
                          activeTab === "sell" &&
                          orderType === "limit" &&
                          value
                        ) {
                          const currentPrice = getCurrentPrice();

                          // Check if we have a valid current price
                          if (currentPrice <= 0) {
                            return "Unable to determine current market price. Please wait...";
                          }

                          const minAllowedPrice = currentPrice * 0.9; // 90% of current price
                          const enteredPrice = parseFloat(value);

                          if (enteredPrice < minAllowedPrice) {
                            return `Price too low! Minimum allowed: ${formatNumber(
                              minAllowedPrice,
                              2
                            )} ${
                              selectedPair?.split("-")[1] || "USDT"
                            } (90% of ${formatNumber(currentPrice, 2)})`;
                          }
                        }
                        return true;
                      },
                    })}
                    type="number"
                    step="0.01"
                    className={`w-full px-3 sm:px-4 py-3 sm:py-4 border border-[#2A356D] ${
                      errors.price ? "border-red-500" : "border-gray-600/50"
                    } rounded-lg sm:rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 ${
                      errors.price
                        ? "focus:ring-red-500/50 focus:border-red-500"
                        : "focus:ring-blue-500/50 focus:border-blue-500/50"
                    } transition-all duration-300 pr-16 sm:pr-20 text-sm sm:text-lg font-medium shadow-inner`}
                    placeholder="0.00"
                    autoComplete="off"
                    style={{
                      WebkitAppearance: "none",
                      MozAppearance: "textfield",
                      backgroundColor: "#ffffff",
                      color: "#000000",
                    }}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-xs sm:text-sm !text-white font-medium">
                      {selectedPair?.split("-")[1] || "USDT"}
                    </span>
                  </div>
                </div>
                {errors.price && (
                  <div className="mt-1 p-1 ">
                    <p className="text-xs font-semibold text-red-400">
                      ❌ {errors.price.message}
                    </p>
                  </div>
                )}
                {/* Show minimum price hint for sell orders */}
                {activeTab === "sell" &&
                  orderType === "limit" &&
                  !errors.price &&
                  getCurrentPrice() > 0 && (
                    <div className="mt-1 p-1 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                      <p className="text-xs text-blue-400">
                        ℹ️ Minimum sell price:{" "}
                        <strong>
                          {formatNumber(getCurrentPrice() * 0.9, 2)}{" "}
                          {selectedPair?.split("-")[1] || "USDT"}
                        </strong>{" "}
                        (90% of current price:{" "}
                        {formatNumber(getCurrentPrice(), 2)})
                      </p>
                    </div>
                  )}
              </div>
            )}

            {/* Quantity Input */}
            <div>
              <div className="flex justify-between items-center mb-2 sm:mb-3">
                <label className="text-sm sm:text-base font-semibold text-white flex items-center">
                  <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full mr-2"></span>
                  Quantity
                </label>
                <span className="text-xs text-white sm:text-white">
                  Available:{" "}
                  {formatNumber(
                    activeTab === "buy"
                      ? balances[selectedPair?.split("-")[1]]?.available || 0
                      : balances[selectedPair?.split("-")[0]]?.available || 0,
                    2
                  )}{" "}
                  {activeTab === "buy"
                    ? selectedPair?.split("-")[1]
                    : selectedPair?.split("-")[0]}
                </span>
              </div>
              <div className="relative">
                <input
                  {...register("quantity", {
                    required: "Quantity is required",
                    min: {
                      value: 0.000001,
                      message: "Quantity must be greater than 0",
                    },
                  })}
                  type="number"
                  step="0.000001"
                  className="w-full px-3 sm:px-4 py-3 sm:py-4 border border-[#2A356D] rounded-lg sm:rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 pr-16 sm:pr-20 text-sm sm:text-lg font-medium shadow-inner"
                  placeholder="0.000000"
                  autoComplete="off"
                  style={{
                    WebkitAppearance: "none",
                    MozAppearance: "textfield",
                    backgroundColor: "#ffffff",
                    color: "#000000",
                  }}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-xs sm:text-sm !text-white font-medium">
                    {selectedPair?.split("-")[0] || "BTC"}
                  </span>
                </div>
              </div>
              {errors.quantity && (
                <p className="mt-1 text-xs sm:text-sm text-red-600">
                  {errors.quantity.message}
                </p>
              )}
            </div>

            {/* Total */}
            {!(orderType === "markets" && activeTab === "buy") && (
              <div>
                <label className="block text-sm sm:text-base font-semibold text-white mb-2 sm:mb-3 flex items-center">
                  <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-500 rounded-full mr-2"></span>
                  Total
                  <span className="text-xs text-white ml-2 font-normal hidden sm:inline">
                    (Price × Quantity)
                  </span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    className="w-full px-3 sm:px-4 py-3 sm:py-4 border border-[#2A356D] rounded-lg sm:rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 pr-16 sm:pr-20 text-sm sm:text-lg font-medium shadow-inner cursor-not-allowed"
                    placeholder="0.00"
                    readOnly
                    value={
                      orderType === "market"
                        ? (() => {
                            const currentPrice = getCurrentPrice();
                            const quantity = watchQuantity;
                            const total = quantity
                              ? (currentPrice * parseFloat(quantity)).toFixed(2)
                              : "0.00";
                            return total;
                          })()
                        : watchPrice && watchQuantity
                        ? (
                            parseFloat(watchPrice) * parseFloat(watchQuantity)
                          ).toFixed(2)
                        : "0.00"
                    }
                    style={{
                      WebkitAppearance: "none",
                      MozAppearance: "textfield",
                      backgroundColor: "#ffffff",
                      color: "#000000",
                    }}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-xs sm:text-sm text-white font-medium">
                      {selectedPair?.split("-")[1] || "USD"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Order Summary */}
            {orderType === "market" && (
              <div className="bg-yellow-900/20 border border-[#2A356D] rounded-lg p-2 mt-2 sm:p-3">
                <div className="flex items-center">
                  <div className="text-white text-xs sm:text-sm">
                    <span className="font-medium">Market Order:</span> Order
                    will be executed at the best available price
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="mt-6 sm:mt-8">
              <button
                type="submit"
                disabled={
                  isPlacingOrder ||
                  !selectedPair ||
                  Object.keys(errors).length > 0
                }
                className={`w-full py-3 sm:py-4 px-4 sm:px-6 rounded-lg font-semibold text-sm sm:text-lg transition-all duration-200 border-2 border-transparent shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-lg ${
                  activeTab === "buy"
                    ? "bg-[#0E1696] hover:from-green-600 hover:to-green-700 text-white border-green-400"
                    : "bg-red-600 hover:from-red-600 hover:to-red-700 text-white border-red-400"
                }`}
              >
                {isPlacingOrder ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 h-4 w-4 sm:h-5 sm:w-5 mr-2"></div>
                    <span className="text-xs sm:text-sm">Placing Order...</span>
                  </div>
                ) : (
                  <span className="text-xs sm:text-sm">
                    {activeTab === "buy" ? "Buy" : "Sell"}{" "}
                    {selectedPair?.split("-")[0] || "Crypto"}
                  </span>
                )}
              </button>
              {/* Show additional error message below button if validation fails */}
              {Object.keys(errors).length > 0 && (
                <div className="mt-3 p-3 bg-red-900/20 border border-red-700 rounded-lg text-center">
                  <p className="text-sm text-red-400 font-medium">
                    ⚠️ Please fix the errors above before submitting
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default TradingInterface;
